import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Grid,
  alpha,
  CircularProgress,
  Autocomplete,
  TextField,
  Paper,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ClearIcon from '@mui/icons-material/Clear';
import InputSelect from 'src/components/shared/Form/Select/index.js';
import InputComponent from 'src/components/shared/Form/Input/index.js';
import DateTimePicker from 'src/components/App/DatePicker/index.js';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  addPatientRequest,
  addTokenAppointmentRequest,
  fetchPatientNameRequest,
  getPatientRequest,
  getDoctorProfileByIdSuccess,
  fetchClinicProfileByIdSuccess,
  fetchProfileRequest,
} from '../../store/appActions';
import { getDoctorProfileById, getClinicProfileById, patientLookup } from '../../api/appApi';

import { ReactComponent as CloseIcon } from 'src/assets/images/close-circle.svg';
import { ReactComponent as AddIcon } from 'src/assets/images/add-square.svg';
import ButtonComponent from 'src/components/shared/Button';
import { getUserFromToken } from 'src/DecodeToken.js';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import storage from 'src/utils/storageUtils';
import { getClinicRequest } from 'src/modules/auth/store/authActions.js';

const genderOptions = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
  { label: 'Other', value: 'other' },
];

const NEW_PATIENT_ID = '__new_patient__';

const calculatePatientAge = (dateOfBirth) => {
  if (!dateOfBirth) return '';
  const dob = dayjs(dateOfBirth);
  if (!dob.isValid()) return '';
  const now = dayjs();
  const years = now.diff(dob, 'year');
  const months = now.diff(dob.add(years, 'year'), 'month');
  const days = now.diff(dob.add(years, 'year').add(months, 'month'), 'day');
  if (years > 0) return `${years} Years ${months} Months ${days} Days`;
  if (months > 0) return `${months} Months ${days} Days`;
  return `${days} Days`;
};

const extractConsultingData = (...sources) => {
  for (const src of sources) {
    if (!src) continue;
    const items = [
      src?.profile?.consulting,
      src?.data?.profile?.consulting,
      src?.data?.consulting,
      src?.consulting,
      src?.profile?.clinicSetting,
      src?.data?.profile?.clinicSetting,
      src?.data?.clinicSetting,
      src?.clinicSetting,
      src?.profile?.clinicInfo,
    ];
    for (const s of items) {
      if (s && typeof s === 'object') {
        const newFee = s.newCaseConsultingFees ?? s.consultingFees ?? s.newCaseFees;
        const oldFee = s.oldCaseConsultingFees ?? s.oldCaseFees;
        if (newFee !== undefined && newFee !== null && newFee !== '') {
          return {
            newCaseFees: Number(newFee),
            oldCaseFees: oldFee !== undefined && oldFee !== null && oldFee !== '' ? Number(oldFee) : Number(newFee),
            onePatientAverageTime: s.onePatientAverageTime || 15,
          };
        }
      }
    }
  }
  return { newCaseFees: '', oldCaseFees: '', onePatientAverageTime: 15 };
};

const getAppointmentSlots = (selectedDate, doctorProfile, clinicProfile, gap = 15) => {
  const d = dayjs(selectedDate).isValid() ? dayjs(selectedDate) : dayjs(selectedDate, ['YYYY-MM-DD', 'DD/MM/YYYY']);
  if (!d.isValid()) return [];

  const now = dayjs();
  const isToday = d.format('YYYY-MM-DD') === now.format('YYYY-MM-DD');
  const selectedDay = d.format('dddd').toLowerCase();

  // Extract timings from doctor profile or clinic profile
  const docTimingsList = [
    ...(doctorProfile?.doctorTimings?.timings || []),
    ...(doctorProfile?.data?.doctorTimings?.timings || []),
    ...(doctorProfile?.timings || []),
    ...(clinicProfile?.clinicTiming || []),
    ...(clinicProfile?.data?.clinicTiming || []),
  ];

  const dayTimings = docTimingsList.filter(
    (t) => (t.day?.value || t.day)?.toLowerCase() === selectedDay
  );

  let currentGap = Number(gap) || 15;
  if (currentGap <= 0) currentGap = 15;

  const ranges = dayTimings.length > 0 ? dayTimings : [{ startTime: '08:00 AM', endTime: '08:00 PM' }];

  const minPriorTime = Number(clinicProfile?.clinicSetting?.minimumPriorBookingTime || 0);
  const threshold = now.add(minPriorTime, 'minutes');

  const slots = [];
  const seen = new Set();

  ranges.forEach((timing) => {
    const startTimeStr = timing.startTime || '08:00 AM';
    const endTimeStr = timing.endTime || '08:00 PM';

    const start = dayjs(startTimeStr).isValid()
      ? dayjs(startTimeStr)
      : dayjs(startTimeStr, ['h:mm A', 'hh:mm A', 'HH:mm', 'hA', 'ha']);
    const end = dayjs(endTimeStr).isValid()
      ? dayjs(endTimeStr)
      : dayjs(endTimeStr, ['h:mm A', 'hh:mm A', 'HH:mm', 'hA', 'ha']);

    if (!start.isValid() || !end.isValid()) return;

    let startHour = start.hour();
    let startMinute = start.minute();
    let endHour = end.hour();
    let endMinute = end.minute();

    let current = d.startOf('day').hour(startHour).minute(startMinute);
    const endOfRange = d.startOf('day').hour(endHour).minute(endMinute);

    for (let t = current; t.isBefore(endOfRange); t = t.add(currentGap, 'minutes')) {
      const val24 = t.format('HH:mm');
      const label12 = t.format('hh:mm A');

      // If today: only show slots after current time (+ grace/prior booking threshold)
      if (!isToday || t.isAfter(threshold)) {
        if (!seen.has(val24)) {
          seen.add(val24);
          slots.push({
            label: label12,
            value: label12,
            time24: val24,
          });
        }
      }
    }
  });

  return slots;
};

const SyncFees = ({ newCaseFees, oldCaseFees, isNewPatient, selectedPatientId }) => {
  const { setFieldValue, values } = useFormikContext();

  useEffect(() => {
    if (isNewPatient) {
      if (newCaseFees !== '' && newCaseFees !== undefined && newCaseFees !== null) {
        setFieldValue('consultingFees', newCaseFees);
      }
    } else if (selectedPatientId) {
      const target = oldCaseFees !== '' && oldCaseFees !== undefined && oldCaseFees !== null ? oldCaseFees : newCaseFees;
      if (target !== '' && target !== undefined && target !== null) {
        setFieldValue('consultingFees', target);
      }
    }
  }, [isNewPatient, selectedPatientId, newCaseFees, oldCaseFees, setFieldValue]);

  return null;
};

const SyncTimeSlot = ({ availableSlots }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (availableSlots && availableSlots.length > 0) {
      const match = availableSlots.find((s) => s.value === values.time || s.label === values.time);
      if (!match) {
        setFieldValue('time', availableSlots[0].value);
      }
    } else if (availableSlots && availableSlots.length === 0) {
      setFieldValue('time', '');
    }
  }, [availableSlots, setFieldValue]);

  return null;
};

function AddAppointment({ onSave, onClose, onPatientAdded, isQueue = false, defaultScheduleType = 'appointment' }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [clinicId, setClinicId] = useState(() => localStorage.getItem('selectedClinicId') || '');
  const [localPatients, setLocalPatients] = useState([]);
  const [localFees, setLocalFees] = useState({
    newCaseFees: '',
    oldCaseFees: '',
    onePatientAverageTime: 15,
  });

  const reduxPatients = useSelector((state) => state.app.patientsname || state.app.patient?.data || []);
  const doctorProfile = useSelector((state) => state.app.getDoctProfileById || state.app.doctorProfile);
  const profile = useSelector((state) => state.app.profile);
  const clinicProfile = useSelector((state) => state.app.getClinicProfileById || state.app.clinicProfile);

  const combinedFees = useMemo(() => {
    const fromRedux = extractConsultingData(doctorProfile, profile, clinicProfile);
    const newCaseFees = localFees.newCaseFees !== '' ? localFees.newCaseFees : fromRedux.newCaseFees;
    const oldCaseFees = localFees.oldCaseFees !== '' ? localFees.oldCaseFees : fromRedux.oldCaseFees;
    const onePatientAverageTime = localFees.onePatientAverageTime || fromRedux.onePatientAverageTime || 15;
    return { newCaseFees, oldCaseFees, onePatientAverageTime };
  }, [localFees, doctorProfile, profile, clinicProfile]);

  useEffect(() => {
    const currentId = localStorage.getItem('selectedClinicId');
    if (currentId) {
      setClinicId(currentId);
    } else {
      const token = storage.get('TOKEN') || localStorage.getItem('token');
      if (token) {
        dispatch(getClinicRequest(token, (res) => {
          const list = res?.data || [];
          if (list.length > 0) {
            localStorage.setItem('selectedClinicId', list[0].id);
            setClinicId(list[0].id);
          }
        }));
      }
    }
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const userId = getUserFromToken();
        const curClinicId = clinicId || localStorage.getItem('selectedClinicId');
        const token = storage.get('TOKEN') || localStorage.getItem('token');

        dispatch(fetchPatientNameRequest(''));
        dispatch(fetchProfileRequest());

        if (token) {
          dispatch(getPatientRequest({ token, params: { limit: 1000, clinicId: curClinicId } }, (res) => {
            if (isMounted && res?.data && Array.isArray(res.data)) {
              setLocalPatients(res.data);
            }
          }));
        }

        let docRes = null;
        let clinicRes = null;

        if (userId) {
          try {
            docRes = await getDoctorProfileById(userId);
            if (docRes) {
              const pData = docRes.profile || docRes.data || docRes;
              dispatch(getDoctorProfileByIdSuccess(pData));
            }
          } catch (e) {
            console.error('Error fetching doctor profile:', e);
          }
        }

        if (curClinicId) {
          try {
            clinicRes = await getClinicProfileById(curClinicId);
            if (clinicRes) {
              const cData = clinicRes.profile || clinicRes.data || clinicRes;
              dispatch(fetchClinicProfileByIdSuccess(cData));
            }
          } catch (e) {
            console.error('Error fetching clinic profile:', e);
          }
        }

        const extracted = extractConsultingData(docRes, clinicRes);
        if (isMounted && (extracted.newCaseFees !== '' || extracted.oldCaseFees !== '')) {
          setLocalFees(extracted);
        }
      } catch (error) {
        console.error('Failed to load appointment form data:', error);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [dispatch, clinicId]);

  // Dynamic search / lookup by phone or name when typing
  useEffect(() => {
    const cleanSearch = (searchQuery || '').trim();
    if (!cleanSearch || cleanSearch.length < 3) return;

    let isSubscribed = true;
    const isNumeric = /^\d+$/.test(cleanSearch);
    const curClinicId = clinicId || localStorage.getItem('selectedClinicId');

    const timer = setTimeout(async () => {
      try {
        if (isNumeric) {
          const res = await patientLookup(cleanSearch, curClinicId);
          const foundList = res?.patients || res?.data || (res?.patient ? [res.patient] : []);
          if (isSubscribed && Array.isArray(foundList) && foundList.length > 0) {
            setLocalPatients((prev) => {
              const prevMap = new Map((prev || []).map((p) => [p.id || p._id, p]));
              foundList.forEach((p) => {
                const id = p.id || p._id;
                if (id) {
                  prevMap.set(id, { ...prevMap.get(id), ...p });
                }
              });
              return Array.from(prevMap.values());
            });
          }
        }
      } catch (err) {
        console.error('Error during dynamic patient search:', err);
      }
    }, 250);

    return () => {
      isSubscribed = false;
      clearTimeout(timer);
    };
  }, [searchQuery, clinicId]);

  // Merge patients from redux and local state
  const allPatientsList = useMemo(() => {
    const rawList = localPatients.length > 0 ? localPatients : (Array.isArray(reduxPatients) ? reduxPatients : []);
    const map = new Map();
    rawList.forEach((p) => {
      const id = p.id || p._id;
      if (id && !map.has(id)) {
        map.set(id, p);
      }
    });
    return Array.from(map.values());
  }, [localPatients, reduxPatients]);

  // Build Autocomplete options with dynamic "+ Add New Patient" option matching search query
  const autocompleteOptions = useMemo(() => {
    const cleanSearch = (searchQuery || '').trim();
    const isNumeric = /^\d+$/.test(cleanSearch);

    const newPatientOption = {
      id: NEW_PATIENT_ID,
      isNew: true,
      label: cleanSearch ? `Add New Patient ("${cleanSearch}")` : 'Add New Patient',
      phoneValue: isNumeric ? cleanSearch : '',
      nameValue: !isNumeric && cleanSearch ? cleanSearch : '',
      description: cleanSearch ? `Create a new record for ${cleanSearch}` : 'Create a new record for this patient',
    };

    const existingOptions = allPatientsList.map((p) => {
      const id = p.id || p._id;
      const fullName = p.fullName || p.name || 'Unknown Patient';
      const contactNo = p.contactNo || p.phone || '';
      const gender = p.gender || 'Not specified';
      const ageStr = calculatePatientAge(p.dateOfBirth);
      const prefix = p.patientId || p.customId || '';

      const details = [
        gender,
        ageStr || (p.age ? `${p.age} Years` : ''),
        prefix,
        contactNo ? `Ph: ${contactNo}` : '',
      ].filter(Boolean).join(' | ');

      return {
        id,
        isNew: false,
        label: fullName,
        contactNo,
        gender: p.gender || 'male',
        dateOfBirth: p.dateOfBirth || null,
        details,
        raw: p,
      };
    });

    return [newPatientOption, ...existingOptions];
  }, [allPatientsList, searchQuery]);

  const isNewPatient = selectedOption?.id === NEW_PATIENT_ID;
  const isExistingSelected = selectedOption && !selectedOption.isNew;

  const initialSlots = getAppointmentSlots(
    dayjs(),
    doctorProfile,
    clinicProfile,
    combinedFees.onePatientAverageTime
  );

  const initialValues = {
    patientId: '',
    fullName: '',
    contactNo: '',
    gender: 'male',
    dateOfBirth: null,
    date: dayjs(),
    time: initialSlots.length > 0 ? initialSlots[0].value : dayjs().add(15, 'minute').format('hh:mm A'),
    addnotes: '',
    consultingFees: combinedFees.newCaseFees !== '' ? combinedFees.newCaseFees : '',
  };

  const validationSchema = Yup.object().shape({
    fullName: Yup.string().when('$isNewPatient', {
      is: true,
      then: (schema) => schema.required('Enter Patient Name'),
      otherwise: (schema) => schema.notRequired(),
    }),

    contactNo: Yup.string().when('$isNewPatient', {
      is: true,
      then: (schema) =>
        schema
          .required('Enter Phone Number')
          .matches(/^\d{10}$/, 'Contact number must be exactly 10 digits'),
      otherwise: (schema) => schema.notRequired(),
    }),

    dateOfBirth: Yup.date()
      .nullable()
      .when('$isNewPatient', {
        is: true,
        then: (schema) => schema.required('Select Date of Birth').max(new Date(), 'Date of Birth cannot be in the future'),
        otherwise: (schema) => schema.notRequired(),
      }),

    gender: Yup.string().when('$isNewPatient', {
      is: true,
      then: (schema) => schema.required('Select Gender'),
      otherwise: (schema) => schema.notRequired(),
    }),

    date: Yup.date().required('Select Appointment Date'),
    time: Yup.string().required('Select Appointment Time'),
    addnotes: Yup.string().required('Enter Symptoms / Reason for visit'),
  });

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    if (!selectedOption) {
      enqueueSnackbar('Please search and select a patient or click Add New Patient', { variant: 'error' });
      return;
    }

    const userId = getUserFromToken();
    if (!userId) {
      enqueueSnackbar('Failed to identify user. Please login again.', { variant: 'error' });
      return;
    }

    try {
      setSubmitting(true);
      let effectiveClinic = clinicId || localStorage.getItem('selectedClinicId');
      if (!effectiveClinic || effectiveClinic === 'undefined' || effectiveClinic === 'null') {
        const token = storage.get('TOKEN') || localStorage.getItem('token');
        try {
          const res = await new Promise((resolve, reject) => dispatch(getClinicRequest(token, resolve, reject)));
          const list = res?.data || [];
          if (list.length > 0) {
            effectiveClinic = list[0].id;
            localStorage.setItem('selectedClinicId', effectiveClinic);
            setClinicId(effectiveClinic);
          }
        } catch (e) {
          console.error("Clinic fetch error during submit:", e);
        }
      }

      let finalPatientId = selectedOption.id;

      if (isNewPatient) {
        const formattedDob = values.dateOfBirth && dayjs(values.dateOfBirth).isValid()
          ? dayjs(values.dateOfBirth).format('YYYY-MM-DD')
          : dayjs().subtract(20, 'year').format('YYYY-MM-DD');

        const patientData = {
          fullName: values.fullName,
          contactNo: values.contactNo,
          gender: values.gender,
          dateOfBirth: formattedDob,
          users: [userId],
          clinic: typeof effectiveClinic === 'object' ? effectiveClinic?.id : effectiveClinic,
        };

        const createdPatient = await new Promise((resolve, reject) => {
          dispatch(addPatientRequest(patientData, resolve, reject));
        });

        finalPatientId = createdPatient?.patient?.id || createdPatient?.patient?._id || createdPatient?.id || createdPatient?.data?.id;

        if (!finalPatientId) {
          enqueueSnackbar('Failed to create patient. Please try again.', { variant: 'error' });
          setSubmitting(false);
          return;
        }

        enqueueSnackbar('Patient created successfully', { variant: 'success' });
      }

      let formattedTime = dayjs().format('HH:mm');
      let displayTime = dayjs().format('hh:mm A');
      if (values.time) {
        if (dayjs.isDayjs(values.time)) {
          formattedTime = values.time.format('HH:mm');
          displayTime = values.time.format('hh:mm A');
        } else {
          const parsed = dayjs(values.time, ['hh:mm A', 'h:mm A', 'HH:mm', 'h:mma', 'HH:mm:ss']);
          if (parsed.isValid()) {
            formattedTime = parsed.format('HH:mm');
            displayTime = parsed.format('hh:mm A');
          } else {
            formattedTime = values.time;
            displayTime = values.time;
          }
        }
      }

      const effectiveScheduleType = isQueue ? 'token' : defaultScheduleType;
      const effectiveRecordStatus = effectiveScheduleType === 'token' ? 'In Queue' : 'Scheduled';

      const appointmentData = {
        date: dayjs(values.date).format('YYYY-MM-DD'),
        time: formattedTime,
        addnotes: values.addnotes || 'General Consultation',
        scheduleType: effectiveScheduleType,
        recordStatus: effectiveRecordStatus,
        user: userId,
        patient: String(finalPatientId),
        clinic: typeof effectiveClinic === 'object' ? effectiveClinic?.id : String(effectiveClinic),
        consultingFees: values.consultingFees !== '' ? Number(values.consultingFees) : undefined,
      };

      await new Promise((resolve, reject) => {
        dispatch(addTokenAppointmentRequest(appointmentData, resolve, reject));
      });

      enqueueSnackbar(isQueue ? 'Patient added to OPD Queue successfully' : 'Appointment booked successfully', { variant: 'success' });

      // Trigger WhatsApp notification if contact available
      const getPatientPhone = () => {
        if (isNewPatient) return values.contactNo;
        return selectedOption.contactNo || '';
      };
      const phoneDigits = String(getPatientPhone() || '').replace(/\D/g, '');
      if (phoneDigits.length >= 10) {
        const phoneParam = phoneDigits.length === 10 ? `91${phoneDigits}` : phoneDigits;
        const dateText = dayjs(values.date).format('DD-MM-YYYY');
        const msg = `Your appointment is scheduled on ${dateText} at ${displayTime}.`;
        const waUrl = `https://wa.me/${phoneParam}?text=${encodeURIComponent(msg)}`;
        window.open(waUrl, '_blank');
      }

      onSave();
      resetForm();
      setSelectedOption(null);
      setSearchQuery('');
      if (typeof onPatientAdded === 'function') {
        onPatientAdded();
      }
    } catch (error) {
      console.error('error is:', error);
      const msg = error?.response?.data?.message || error?.message || (typeof error === 'string' ? error : 'Failed to add appointment');
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const isDarkMode = theme.palette.mode === 'dark';
  const inputBg = isDarkMode ? alpha(theme.palette.common.white, 0.04) : '#ffffff';
  const inputBorder = isDarkMode ? alpha(theme.palette.common.white, 0.12) : alpha(theme.palette.common.black, 0.12);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      context={{ isNewPatient }}
    >
      {({ values, errors, touched, handleBlur, setFieldValue, isSubmitting }) => {
        return (
          <Form style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            <SyncFees
              newCaseFees={combinedFees.newCaseFees}
              oldCaseFees={combinedFees.oldCaseFees}
              isNewPatient={isNewPatient}
              selectedPatientId={selectedOption?.id}
            />
            <SyncTimeSlot
              availableSlots={getAppointmentSlots(
                values.date,
                doctorProfile,
                clinicProfile,
                combinedFees.onePatientAverageTime
              )}
            />

            {/* Dialog Header */}
            <Box
              sx={{
                px: 3,
                py: 1.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.15) : theme.palette.primary.light3 || '#f0f4ff',
                borderBottom: `1px solid ${theme.palette.divider}`,
                flexShrink: 0,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  color: isDarkMode ? theme.palette.text.primary : theme.palette.primary.main || '#1e293b',
                }}
              >
                {isQueue ? 'Add Patient to OPD Queue' : 'Add Appointment'}
              </Typography>
              <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
                <CloseIcon width={20} height={20} />
              </IconButton>
            </Box>

            {/* Scrollable Form Body */}
            <Box
              sx={{
                p: { xs: 2, sm: 3 },
                display: 'flex',
                flexDirection: 'column',
                gap: 2.2,
                overflowY: 'auto',
                flex: 1,
                maxHeight: 'calc(88vh - 135px)',
                bgcolor: 'background.paper',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: alpha(theme.palette.text.primary, 0.18),
                  borderRadius: '3px',
                },
              }}
            >
              {/* Doctor-style Search Patient Autocomplete */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Search Patient (Phone / Name) <span style={{ color: theme.palette.error.main }}>*</span>
                </Typography>

                <Autocomplete
                  openOnFocus
                  value={selectedOption}
                  onChange={(event, newValue) => {
                    setSelectedOption(newValue);
                    if (newValue?.isNew) {
                      if (newValue.phoneValue) {
                        setFieldValue('contactNo', newValue.phoneValue);
                      }
                      if (newValue.nameValue) {
                        setFieldValue('fullName', newValue.nameValue);
                      }
                    } else if (newValue && !newValue.isNew) {
                      setFieldValue('patientId', newValue.id);
                    } else {
                      setFieldValue('patientId', '');
                    }
                  }}
                  inputValue={searchQuery}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason !== 'reset') {
                      setSearchQuery(newInputValue);
                    }
                  }}
                  options={autocompleteOptions}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option?.label || '';
                  }}
                  filterOptions={(options, state) => {
                    const q = state.inputValue.toLowerCase().trim();
                    if (!q) return options;
                    return options.filter((opt) => {
                      if (opt.isNew) return true;
                      const name = (opt.label || '').toLowerCase();
                      const phone = (opt.contactNo || '').replace(/\D/g, '');
                      const details = (opt.details || '').toLowerCase();
                      return name.includes(q) || phone.includes(q) || details.includes(q);
                    });
                  }}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  PaperComponent={(props) => (
                    <Paper
                      {...props}
                      sx={{
                        borderRadius: '12px',
                        mt: 1,
                        boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                        border: `1px solid ${theme.palette.divider}`,
                        maxHeight: '320px',
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    if (option.isNew) {
                      return (
                        <Box
                          component="li"
                          {...props}
                          key={option.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 1.75,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.04),
                            '&:hover': {
                              bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.main, 0.08),
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: '8px',
                              bgcolor: 'primary.main',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <PersonAddAlt1OutlinedIcon fontSize="small" />
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              {option.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              {option.description}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }

                    return (
                      <Box
                        component="li"
                        {...props}
                        key={option.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                          '&:hover': {
                            bgcolor: isDarkMode ? alpha(theme.palette.common.white, 0.06) : alpha(theme.palette.common.black, 0.03),
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            mt: 0.25,
                          }}
                        >
                          <PersonOutlineIcon fontSize="small" />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {option.label}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.25 }}>
                            {option.details}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Type phone number or patient name..."
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: 'text.secondary', ml: 0.5 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: inputBg,
                          height: '46px',
                          borderRadius: '10px',
                          border: `1px solid ${!selectedOption && touched.fullName ? theme.palette.error.main : inputBorder}`,
                        },
                      }}
                    />
                  )}
                />
              </Box>

              {/* Existing Patient Selected Banner */}
              {isExistingSelected && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '10px',
                    bgcolor: isDarkMode ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.success.main, 0.06),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PersonOutlineIcon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {selectedOption.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        {selectedOption.details}
                      </Typography>
                    </Box>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedOption(null);
                      setSearchQuery('');
                      setFieldValue('patientId', '');
                    }}
                    sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {/* New Patient Form Fields (Shown when + Add New Patient is active) */}
              {isNewPatient && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    bgcolor: isDarkMode ? alpha(theme.palette.common.white, 0.02) : alpha(theme.palette.primary.main, 0.02),
                    border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonAddAlt1OutlinedIcon fontSize="small" /> New Patient Registration
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedOption(null);
                        setSearchQuery('');
                      }}
                      sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          New Patient Name <span style={{ color: theme.palette.error.main }}>*</span>
                        </Typography>
                        <InputComponent
                          name="fullName"
                          variant="outlined"
                          value={values.fullName}
                          onBlur={handleBlur}
                          onChange={(e) => setFieldValue('fullName', e.target.value)}
                          error={touched.fullName && Boolean(errors.fullName)}
                          helperText={touched.fullName && errors.fullName}
                          placeholder="Enter Full Name"
                          fullWidth
                          sx={{
                            backgroundColor: inputBg,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '10px',
                              height: '44px',
                              border: `1px solid ${inputBorder}`,
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Phone Number <span style={{ color: theme.palette.error.main }}>*</span>
                        </Typography>
                        <InputComponent
                          name="contactNo"
                          variant="outlined"
                          value={values.contactNo}
                          onBlur={handleBlur}
                          onChange={(e) => setFieldValue('contactNo', e.target.value.replace(/\D/g, '').slice(0, 10))}
                          error={touched.contactNo && Boolean(errors.contactNo)}
                          helperText={touched.contactNo && errors.contactNo}
                          placeholder="Enter 10-digit Number"
                          fullWidth
                          sx={{
                            backgroundColor: inputBg,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '10px',
                              height: '44px',
                              border: `1px solid ${inputBorder}`,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Gender <span style={{ color: theme.palette.error.main }}>*</span>
                        </Typography>
                        <InputSelect
                          name="gender"
                          showNone={false}
                          value={values.gender}
                          onChange={(val) => setFieldValue('gender', val)}
                          options={genderOptions}
                          onBlur={handleBlur}
                          placeholder="Select Gender"
                          variant="outlined"
                          error={touched.gender && Boolean(errors.gender)}
                          helperText={touched.gender && errors.gender}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: inputBg,
                              height: '44px',
                              borderRadius: '10px',
                              border: `1px solid ${inputBorder}`,
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Date of Birth <span style={{ color: theme.palette.error.main }}>*</span>
                        </Typography>
                        <DateTimePicker
                          name="dateOfBirth"
                          value={values.dateOfBirth ? dayjs(values.dateOfBirth) : null}
                          onBlur={handleBlur}
                          onChange={(date) => setFieldValue('dateOfBirth', date ? dayjs(date).toDate() : null)}
                          type="date"
                          error={touched.dateOfBirth && Boolean(errors.dateOfBirth)}
                          helperText={touched.dateOfBirth && errors.dateOfBirth}
                          sx={{
                            height: '44px',
                            borderRadius: '10px',
                            backgroundColor: inputBg,
                            border: `1px solid ${inputBorder}`,
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Appointment Date & Timeslot */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Appointment Date <span style={{ color: theme.palette.error.main }}>*</span>
                    </Typography>
                    <DateTimePicker
                      value={values.date}
                      onBlur={handleBlur}
                      onChange={(date) => setFieldValue('date', date)}
                      type="date"
                      error={touched.date && Boolean(errors.date)}
                      helperText={touched.date && errors.date}
                      sx={{
                        height: '44px',
                        borderRadius: '10px',
                        backgroundColor: inputBg,
                        border: `1px solid ${inputBorder}`,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Appointment Timeslot <span style={{ color: theme.palette.error.main }}>*</span>
                    </Typography>
                    {(() => {
                      const slots = getAppointmentSlots(
                        values.date,
                        doctorProfile,
                        clinicProfile,
                        combinedFees.onePatientAverageTime
                      );
                      return (
                        <FormControl fullWidth size="small" error={touched.time && Boolean(errors.time)}>
                          <Select
                            name="time"
                            value={values.time || ''}
                            onChange={(e) => setFieldValue('time', e.target.value)}
                            onBlur={handleBlur}
                            displayEmpty
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 280,
                                  borderRadius: '10px',
                                  boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                                  '& .MuiMenuItem-root': {
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    py: 1,
                                    '&.Mui-selected': {
                                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                                      color: theme.palette.primary.main,
                                      fontWeight: 700,
                                    },
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    },
                                  },
                                },
                              },
                            }}
                            sx={{
                              height: '44px',
                              borderRadius: '10px',
                              backgroundColor: inputBg,
                              border: `1px solid ${inputBorder}`,
                              fontSize: '14px',
                            }}
                          >
                            {slots.length === 0 ? (
                              <MenuItem disabled value="">
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                                  No slots available for this date
                                </Typography>
                              </MenuItem>
                            ) : (
                              slots.map((slot) => (
                                <MenuItem key={slot.value} value={slot.value}>
                                  {slot.label}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                          {touched.time && errors.time && (
                            <FormHelperText error sx={{ ml: 0.5 }}>{errors.time}</FormHelperText>
                          )}
                        </FormControl>
                      );
                    })()}
                  </Box>
                </Grid>
              </Grid>

              {/* Consulting Fees */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Consulting Fees (₹)
                  </Typography>
                  {(combinedFees.newCaseFees !== '' || combinedFees.oldCaseFees !== '') && (
                    <Typography variant="caption" sx={{ color: 'primary.main', fontSize: '0.72rem', fontWeight: 600 }}>
                      {isNewPatient ? `New Case Default: ₹${combinedFees.newCaseFees || 0}` : `Existing Patient Default: ₹${combinedFees.oldCaseFees || combinedFees.newCaseFees || 0}`}
                    </Typography>
                  )}
                </Box>
                <InputComponent
                  name="consultingFees"
                  type="number"
                  variant="outlined"
                  value={values.consultingFees}
                  onBlur={handleBlur}
                  onChange={(e) => setFieldValue('consultingFees', e.target.value)}
                  error={touched.consultingFees && Boolean(errors.consultingFees)}
                  helperText={touched.consultingFees && errors.consultingFees}
                  placeholder="Enter Consulting Fees"
                  fullWidth
                  sx={{
                    backgroundColor: inputBg,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      height: '44px',
                      border: `1px solid ${inputBorder}`,
                    },
                  }}
                />
              </Box>

              {/* Symptoms */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Symptoms <span style={{ color: theme.palette.error.main }}>*</span>
                </Typography>
                <InputComponent
                  name="addnotes"
                  value={values.addnotes}
                  onBlur={handleBlur}
                  onChange={(e) => setFieldValue('addnotes', e.target.value)}
                  placeholder="Enter Symptoms / Reason for visit"
                  multiline
                  rows={3}
                  variant="outlined"
                  fullWidth
                  error={touched.addnotes && Boolean(errors.addnotes)}
                  helperText={touched.addnotes && errors.addnotes}
                  sx={{
                    backgroundColor: inputBg,
                    '& .MuiOutlinedInput-root': {
                      padding: '12px 14px',
                      borderRadius: '10px',
                      border: `1px solid ${inputBorder}`,
                      alignItems: 'flex-start',
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Dialog Footer Actions */}
            <Box
              sx={{
                px: 3,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1.5,
                borderTop: `1px solid ${theme.palette.divider}`,
                bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.95) : '#fbfbfb',
                flexShrink: 0,
              }}
            >
              <ButtonComponent
                onClick={onClose}
                variant="none"
                disabled={isSubmitting}
                startIcon={<CloseIcon width={18} height={18} />}
                sx={{
                  borderRadius: '10px',
                  px: 2,
                  py: 0.8,
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.text.primary, 0.05),
                  },
                }}
              >
                Cancel
              </ButtonComponent>

              <ButtonComponent
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AddIcon width={18} height={18} />
                  )
                }
                sx={{
                  borderRadius: '10px',
                  px: 3,
                  py: 0.8,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  bgcolor: theme.palette.primary.main,
                  color: '#ffffff',
                  boxShadow: '0 2px 8px rgba(79, 70, 229, 0.25)',
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                  },
                }}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </ButtonComponent>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}

export default AddAppointment;
